const SEQUENCE_BRANDS = new Set(['msf1', 'hevc', 'hevx'])

export const HeicErrorCode = {
  SEQUENCE_NOT_SUPPORTED: 'SEQUENCE_NOT_SUPPORTED',
  CONVERSION_FAILED: 'CONVERSION_FAILED',
} as const

type HeicErrorCodeValue = (typeof HeicErrorCode)[keyof typeof HeicErrorCode]

interface HeicConversionError extends Error {
  code: HeicErrorCodeValue
  cause?: unknown
}

interface NormalizeImageFileResult {
  file: File
  converted: boolean
  previewUrl: string | null
}

let heicToModule: typeof import('heic-to') | null = null

const loadHeicTo = async () => {
  if (!heicToModule) {
    heicToModule = await import('heic-to')
  }
  return heicToModule
}

const getFileBrand = async (file: File) => {
  const buffer = await file.arrayBuffer()
  return new TextDecoder('utf-8')
    .decode(buffer.slice(8, 12))
    .replace('\0', ' ')
    .trim()
}

export const isHeicExtension = (filename: string) =>
  /\.(heic|heif)$/i.test(filename)

export const toJpegFilename = (filename: string) => {
  if (/\.(heic|heif)$/i.test(filename)) {
    return `${filename}.jpg`
  }

  const dotIndex = filename.lastIndexOf('.')
  const base = dotIndex > 0 ? filename.slice(0, dotIndex) : filename
  return `${base}.jpg`
}

export const isHeicFile = async (file: File) => {
  if (isHeicExtension(file.name)) {
    return true
  }

  const { isHeic } = await loadHeicTo()
  return isHeic(file)
}

const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
])

const ALLOWED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i

export const IMAGE_FILE_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif'

export const isAllowedImageFile = async (file: File) => {
  const mime = file.type.toLowerCase()

  if (mime && ALLOWED_IMAGE_TYPES.has(mime)) {
    return true
  }

  if (isHeicExtension(file.name) || (await isHeicFile(file))) {
    return true
  }

  if (!mime && ALLOWED_IMAGE_EXTENSIONS.test(file.name)) {
    return true
  }

  return false
}

export const normalizeImageFile = async (
  file: File,
  { quality = 0.92 }: { quality?: number } = {}
): Promise<NormalizeImageFileResult> => {
  const isHeic = await isHeicFile(file)

  if (!isHeic) {
    return { file, converted: false, previewUrl: null }
  }

  const brand = await getFileBrand(file)
  if (SEQUENCE_BRANDS.has(brand)) {
    const error = new Error(
      HeicErrorCode.SEQUENCE_NOT_SUPPORTED
    ) as HeicConversionError
    error.code = HeicErrorCode.SEQUENCE_NOT_SUPPORTED
    throw error
  }

  try {
    const { heicTo } = await loadHeicTo()
    const blob = await heicTo({
      blob: file,
      type: 'image/jpeg',
      quality,
    })
    const jpegFile = new File([blob], toJpegFilename(file.name), {
      type: 'image/jpeg',
    })
    const previewUrl = URL.createObjectURL(jpegFile)

    return { file: jpegFile, converted: true, previewUrl }
  } catch (err) {
    if (
      err &&
      typeof err === 'object' &&
      'code' in err &&
      err.code === HeicErrorCode.SEQUENCE_NOT_SUPPORTED
    ) {
      throw err
    }

    const error = new Error(
      HeicErrorCode.CONVERSION_FAILED
    ) as HeicConversionError
    error.code = HeicErrorCode.CONVERSION_FAILED
    error.cause = err
    throw error
  }
}

export const revokePreviewUrl = (previewUrl: string | null) => {
  if (previewUrl) {
    URL.revokeObjectURL(previewUrl)
  }
}
